// ClipRadar website — the launch-film section.
//
// The founder on camera, walking through the app: a credibility surface, not a
// feature pitch. Two rules shaped it:
//  - NOT LIVE by default (founder, 2026-08-02): `LAUNCH_FILM_LIVE` gates it;
//    `?launchvideo=1` previews it anywhere, including production, so the
//    founder can judge it on the real site before flipping the flag.
//  - Nothing loads from YouTube until the visitor presses play — the poster is
//    a bundled asset, and the click swaps in a youtube-nocookie embed. That
//    keeps the site's no-third-party-before-consent posture intact.
//
// The frame is the site's monitor language (the DesktopApp window, the scene
// stills): one lit object on the dark scroll, not a boxed video player. A
// single mono chip on the glass states what it is; the play control is the
// only accent.
import { useState } from 'react';
import poster from '../assets/launch-video-poster.jpg';
import { CRWIcon } from '../components/icons';

const LAUNCH_FILM_LIVE = false; // hidden again (founder, 2026-08-02) until a better thumbnail is set; preview with ?launchvideo=1
const VIDEO_ID = 'qgFOPgDEgVc';

export function LaunchFilm() {
  const [playing, setPlaying] = useState(false);

  const previewed = new URLSearchParams(window.location.search).get('launchvideo') === '1';
  if (!LAUNCH_FILM_LIVE && !previewed) return null;

  return (
    <section id="launch-film" aria-labelledby="crw-film-h" style={{ padding: '96px 24px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, marginBottom: 40 }}>
          <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
            Launch film
          </span>
          <h2 id="crw-film-h" className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(28px, 3.8vw, 44px)', letterSpacing: 'var(--tracking-display)', lineHeight: 1.1, textWrap: 'balance' }}>
            Straight from the editing desk.
          </h2>
          <p style={{ margin: 0, maxWidth: 576, fontSize: 15, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'balance' }}>
            Three and a half minutes with the person building ClipRadar — real searches, on real footage.
          </p>
        </header>

        {/* the frame: one lit monitor on the dark scroll */}
        <div style={{ position: 'relative' }}>
          {/* signal bloom behind the glass — light, not paint */}
          <div aria-hidden="true" style={{ position: 'absolute', inset: '-40px -60px', background: 'radial-gradient(ellipse at 50% 60%, var(--signal-glow), transparent 65%)', filter: 'blur(40px)', opacity: 0.5, pointerEvents: 'none' }}></div>

          <div className="crw-film-frame" style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: 18, overflow: 'hidden', border: '1px solid var(--line-2)', background: '#000', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), 0 30px 80px rgba(0,0,0,0.55)' }}>
            {playing ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
                title="Launch Video | ClipRadar"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              />
            ) : (
              <button
                className="crw-film-facade"
                onClick={() => setPlaying(true)}
                aria-label="Play the ClipRadar launch video"
              >
                <img src={poster} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                {/* gentle vignette so the glass reads as one lit object; the
                    poster carries its own identity — no chips, no labels */}
                <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,6,9,0.22), transparent 28%, transparent 68%, rgba(5,6,9,0.4))' }}></span>

                {/* play control — the only accent on the glass */}
                <span className="crw-film-play" aria-hidden="true">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 3 }}>
                    <path d="M8 5.5v13l11-6.5z" />
                  </svg>
                </span>
              </button>
            )}
          </div>
        </div>

        {/* quiet meta row under the frame */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <a href={`https://www.youtube.com/watch?v=${VIDEO_ID}`} target="_blank" rel="noreferrer" className="crw-film-yt" style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 7, padding: '13px 16px' }}>
            Watch on YouTube · 3:25 <CRWIcon d="arrowRight" size={11} />
          </a>
        </div>
      </div>
    </section>
  );
}
